import { gsap } from 'gsap';

export default class Header {
    constructor(el, app) {
        this.el = el;
        this.app = app;
        this.html = document.documentElement;

        this.options = {
            threshold: 400, // Scroll amount in pixels before hiding
            hide: false, // Enable/Disable hiding behavior
        };

        this.scrollPosition = 0;
        this.lastScrollPosition = 0;
        this.isOpen = false;

        this.toggle = this.toggle.bind(this);

        this.cacheDom();
        this.init();
    }

    /* -----------------------------------------
     * DOM
     * ----------------------------------------- */
    cacheDom() {
        this.dom = {
            html: document.documentElement,
            toggle: this.el.querySelector('.js-toggle-menu'),
            menu: this.el.querySelector('.header__menu'),
            navItems: this.el.querySelectorAll('.nav__item'),
            panel: document.querySelector('.menu-panel'),
            panelInner: document.querySelector('.menu-panel__inner'),
            primaryItems: document.querySelectorAll('.menu-panel__primary li'),
            secondaryItems: document.querySelectorAll('.menu-panel__secondary li'),
            cta: document.querySelectorAll('.menu-panel__tertiary li'),
            utilitiesItems: document.querySelectorAll('.utilities__item'),
        };
    }

    /* -----------------------------------------
     * Init
     * ----------------------------------------- */
    init() {
        this.setOptions();

        window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

        if (!this.dom.toggle || !this.dom.panel) return;

        document.fonts.ready.then(() => {
            this.setMenuWidth();
        });

        window.addEventListener('resize', () => {
            this.setMenuWidth();
        });

        this.initTimeline();

        this.dom.toggle.addEventListener('click', this.toggle);
    }

    setOptions() {
        if ('threshold' in this.el.dataset) {
            this.options.threshold = parseFloat(this.el.dataset.threshold);
        }
        if ('autoHide' in this.el.dataset) {
            this.options.hide = this.el.dataset.hide !== 'false';
        }
    }

    onScroll() {
        this.lastScrollPosition = this.scrollPosition;
        this.scrollPosition = window.scrollY;

        this.setDirections();

        if (this.options.hide) {
            this.setHeaderState();
        }
    }

    setHeaderState() {
        if (
            this.scrollPosition > this.options.threshold &&
            this.scrollPosition > this.lastScrollPosition &&
            !this.isOpen
        ) {
            this.el.classList.add('header-is-hidden');
        } else if (this.scrollPosition < this.lastScrollPosition || this.isOpen) {
            this.el.classList.remove('header-is-hidden');
        }
    }

    setDirections() {
        if (this.scrollPosition > this.lastScrollPosition) {
            this.html.classList.remove('is-scrolling-up');
            this.html.classList.add('is-scrolling-down');
        } else {
            this.html.classList.remove('is-scrolling-down');
            this.html.classList.add('is-scrolling-up');
        }
    }

    /* -----------------------------------------
     * GSAP
     * ----------------------------------------- */
    initTimeline() {
        gsap.set(this.dom.navItems, {
            opacity: 1,
            x: 0,
        });
        gsap.set(
            [
                ...this.dom.primaryItems,
                ...this.dom.secondaryItems,
                ...this.dom.cta,
                ...this.dom.utilitiesItems,
            ],
            {
                opacity: 0,
                y: -20,
            }
        );

        this.tlMenu = gsap.timeline({
            paused: true,
            defaults: { ease: 'power3.out' },
            onReverseComplete: () => {
                this.dom.panel.setAttribute('aria-hidden', 'true');
            },
        });

        this.tlMenu

            // Nav items
            .to(this.dom.navItems, {
                opacity: 0,
                x: -10,
                duration: 0.3,
                stagger: 0.05,
                ease: 'power4.in',
            })

            // Utilities items
            .to(this.dom.utilitiesItems, {
                opacity: 1,
                y: 0,
                duration: 0.3,
            })

            // Primary items
            .to(
                this.dom.primaryItems,
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    stagger: 0.08,
                },
                '-=0.1'
            )

            // Secondary items
            .to(
                this.dom.secondaryItems,
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    stagger: 0.06,
                },
                '-=0.25'
            )

            // CTA
            .to(
                this.dom.cta,
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                },
                '-=0.2'
            );
    }

    /* -----------------------------------------
     * Actions
     * ----------------------------------------- */
    toggle() {
        this.isOpen ? this.close() : this.open();
        this.isOpen = !this.isOpen;
    }

    open() {
        this.dom.html.classList.add('nav-is-active');
        this.dom.panel.classList.add('is-active');

        this.dom.toggle.setAttribute('aria-expanded', 'true');
        this.dom.panel.setAttribute('aria-hidden', 'false');

        this.tlMenu.play();
    }

    close() {
        this.dom.html.classList.remove('nav-is-active');
        this.dom.panel.classList.remove('is-active');

        this.dom.toggle.setAttribute('aria-expanded', 'false');

        this.tlMenu.reverse();
    }

    /* -----------------------------------------
     * Utils
     * ----------------------------------------- */
    setMenuWidth() {
        if (this.dom.menu) {
            const width = this.dom.menu.offsetWidth;
            this.dom.html.style.setProperty('--menu-width', `${width}px`);
        }
    }

    /* -----------------------------------------
     * Destroy
     * ----------------------------------------- */
    kill() {
        this.dom.toggle.removeEventListener('click', this.toggle);
        window.removeEventListener('resize', this.setHeaderHeight);
        this.tlMenu.kill();
    }
}
